import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Clock, X } from 'lucide-react';
import { apiUrl } from '@/lib/api-client';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import CategoryAccordion from './CategoryAccordion';
import FilterSearchList from './FilterSearchList';

interface FilterState {
  category?: string;
  subcategory?: string;
  manufacturers: Set<string>;
  languages: string[];
}

interface ManualsSidebarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  searchHistory?: string[];
  onSearchHistorySelect?: (term: string) => void;
  categories: Array<{ id: string; code: string; name: string; display_order: number }>;
  subcategories: Array<{ id: string; category_id: string; code: string; name: string; display_order: number }>;
  manufacturers: Array<{ id: string; name: string; logo_url?: string; display_order: number }>;
  languages: string[];
  categoryCounts?: Record<string, number>;
  isLoadingCategories?: boolean;
  isLoadingManufacturers?: boolean;
  /** Kompakt nézet (pl. mobil szűrő sheet) – kevesebb padding */
  compact?: boolean;
}

export default function ManualsSidebar({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  searchHistory = [],
  onSearchHistorySelect,
  categories,
  subcategories,
  manufacturers,
  languages,
  categoryCounts = {},
  isLoadingCategories = false,
  isLoadingManufacturers = false,
  compact = false,
}: ManualsSidebarProps) {
  const { t } = useTranslation();
  const [searchFocused, setSearchFocused] = useState(false);

  const handleCategorySelect = useCallback((categoryCode: string | undefined) => {
    onFiltersChange({
      ...filters,
      category: categoryCode,
      subcategory: undefined, // Reset subcategory when category changes
    });
  }, [filters, onFiltersChange]);

  const handleSubcategorySelect = useCallback((subcategoryCode: string | undefined) => {
    onFiltersChange({
      ...filters,
      subcategory: subcategoryCode,
    });
  }, [filters, onFiltersChange]);

  const handleManufacturersChange = useCallback((selected: Set<string>) => {
    onFiltersChange({
      ...filters,
      manufacturers: selected,
    });
  }, [filters, onFiltersChange]);

  const handleLanguageToggle = useCallback((lang: string) => {
    const newLanguages = filters.languages.includes(lang)
      ? filters.languages.filter(l => l !== lang)
      : [...filters.languages, lang];
    onFiltersChange({
      ...filters,
      languages: newLanguages,
    });
  }, [filters, onFiltersChange]);

  const clearFilters = useCallback(() => {
    onFiltersChange({
      category: undefined,
      subcategory: undefined,
      manufacturers: new Set(),
      languages: [],
    });
    onSearchChange('');
  }, [onFiltersChange, onSearchChange]);

  const hasActiveFilters = filters.category || filters.subcategory || 
    filters.manufacturers.size > 0 || filters.languages.length > 0;

  // Prepare manufacturer items with IDs (use name as ID for now)
  const manufacturerItems = manufacturers.map(m => ({
    id: m.name,
    name: m.name,
  }));

  return (
    <div className={cn(compact ? "space-y-2" : "space-y-4")}>
      {/* Search Card */}
      <div className={cn("border rounded-md bg-card", compact ? "p-2 space-y-2" : "p-4 space-y-4")}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("manuals.searchManuals")}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            className="pl-10 border border-border focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border focus-visible:outline-none"
          />
          {searchFocused && searchHistory.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {searchHistory.map((term, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (onSearchHistorySelect) {
                      onSearchHistorySelect(term);
                    }
                    setSearchFocused(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2"
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{term}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            {t("manualsExtra.clearFilters")}
          </Button>
        )}
      </div>

      {/* Categories Card – same box as Brands/Languages for consistency */}
      <div className={cn("border rounded-md bg-card", compact ? "p-2" : "p-4")}>
        <CategoryAccordion
          categories={categories}
          subcategories={subcategories}
          selectedCategory={filters.category}
          selectedSubcategory={filters.subcategory}
          onCategorySelect={handleCategorySelect}
          onSubcategorySelect={handleSubcategorySelect}
          categoryCounts={categoryCounts}
          isLoading={isLoadingCategories}
        />
      </div>

      {/* Manufacturers Card */}
      <div className={cn("border rounded-md bg-card", compact ? "p-2" : "p-4")}>
        <FilterSearchList
          title={t("manuals.brandsTitle")}
          items={manufacturerItems}
          selected={filters.manufacturers}
          onSelectionChange={handleManufacturersChange}
          placeholder={t("manuals.searchBrands")}
        />
      </div>

      {/* Languages Card */}
      {languages.length > 0 && (
        <div className={cn("border rounded-md bg-card", compact ? "p-2" : "p-4")}>
          <div className="space-y-2">
            <div className="text-sm font-medium">Nyelvek</div>
            <div className="flex flex-wrap gap-2">
              {languages.map(lang => {
                const isSelected = filters.languages.includes(lang);
                const LANGUAGE_FLAGS: Record<string, string> = {
                  'HU': '🇭🇺',
                  'SK': '🇸🇰',
                  'CZ': '🇨🇿',
                  'EN': '🇬🇧',
                  'DE': '🇩🇪',
                  'PL': '🇵🇱',
                };
                const LANGUAGE_NAMES: Record<string, string> = {
                  'HU': 'Magyar',
                  'SK': 'Slovenský',
                  'CZ': 'Český',
                  'EN': 'English',
                  'DE': 'Deutsch',
                  'PL': 'Polski',
                };
                return (
                  <button
                    key={lang}
                    onClick={() => handleLanguageToggle(lang)}
                    className={cn(
                      'px-3 py-1 rounded-md text-sm flex items-center gap-2',
                      isSelected ? 'bg-primary/10 text-primary' : 'bg-muted/50'
                    )}
                  >
                    <span>{LANGUAGE_FLAGS[lang] || '🌐'}</span>
                    <span>{LANGUAGE_NAMES[lang] || lang}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

